import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Routes, Route, Navigate } from 'react-router-dom';
import { apiLoginRequest } from './auth/msal.ts';
import { AppShell } from './components/AppShell.tsx';
import { PendingApprovalScreen } from './components/PendingApprovalScreen.tsx';
import { DashboardPage } from './pages/Dashboard.tsx';
import { SharedKeysPage } from './pages/SharedKeys.tsx';
import { ParentKeysPage } from './pages/ParentKeys.tsx';
import { LogsPage } from './pages/Logs.tsx';
import { UsersPage } from './pages/Users.tsx';
import { useI18n } from './i18n/context.tsx';
import { useMe } from './auth/useMe.ts';

const useStyles = makeStyles({
  signInRoot: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: tokens.colorNeutralBackground2,
  },
  card: {
    background: tokens.colorNeutralBackground1,
    padding: '48px 56px',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    maxWidth: '420px',
    textAlign: 'center',
    display: 'grid',
    gap: '16px',
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export function App() {
  const styles = useStyles();
  const { instance, inProgress } = useMsal();
  const { t } = useI18n();

  return (
    <>
      <AuthenticatedTemplate>
        <AuthedGate />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className={styles.signInRoot}>
          <div className={styles.card}>
            <Text className={styles.title}>LINE Ads API Proxy</Text>
            <Text>{t('common', 'signInHint')}</Text>
            {inProgress !== 'none' ? (
              <Spinner />
            ) : (
              <Button
                appearance="primary"
                onClick={() => instance.loginRedirect(apiLoginRequest).catch(console.error)}
              >
                {t('common', 'signIn')}
              </Button>
            )}
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

// Fetches /admin/me and routes to either the approval screen or the main app
// shell. Splitting this out of App keeps the query hook from running until MSAL
// has an active account (AuthenticatedTemplate gates it).
function AuthedGate() {
  const styles = useStyles();
  const { t } = useI18n();
  const me = useMe();

  if (me.isLoading) {
    return (
      <div className={styles.signInRoot}>
        <Spinner />
      </div>
    );
  }

  if (me.isError || !me.data) {
    return (
      <div className={styles.signInRoot}>
        <div className={styles.card}>
          <Text className={styles.title}>{t('common', 'saveFailed')}</Text>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{(me.error as Error)?.message ?? 'unknown error'}</MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
          <Button appearance="primary" onClick={() => me.refetch()}>
            {t('common', 'retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (me.data.status !== 'active') {
    return <PendingApprovalScreen me={me.data} />;
  }

  const isAdmin = me.data.role === 'admin';

  return (
    <AppShell isAdmin={isAdmin}>
      <Routes>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/shared-keys" element={<SharedKeysPage />} />
        <Route path="/parent-keys" element={<ParentKeysPage />} />
        <Route path="/logs" element={<LogsPage />} />
        {isAdmin ? <Route path="/users" element={<UsersPage />} /> : null}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}
